import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  FiEdit2,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiFileText,
} from 'react-icons/fi';
import { colors } from '../theme/modernTheme';
import { trustMastersAPI } from '../services/api';
import { showError } from '../utils/toast';
import TableLoader from './TableLoader';
import { formatDateForDisplay } from '../utils/dateUtils';

/**
 * Trust Master List Component
 * Displays paginated list with search and filters
 *
 * @param {Object} props
 * @param {Function} props.onView - Callback when row is clicked to view details
 * @param {Function} props.onEdit - Callback when edit button is clicked
 * @param {Function} props.onDelete - Callback when delete button is clicked
 * @param {Function} props.onPreviewReceipt - Callback when preview receipt is clicked
 * @param {number} [props.refreshTrigger] - Optional trigger to refresh the list
 */
export default function TrustMasterList({
  onView,
  onEdit,
  onDelete,
  onPreviewReceipt,
  refreshTrigger,
}) {
  // Component state
  const [trustMasters, setTrustMasters] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search and filter states
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [dateFromInput, setDateFromInput] = useState('');
  const [dateToInput, setDateToInput] = useState('');

  // Applied filter states (used for API calls)
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Load trust masters on mount and when dependencies change
  useEffect(() => {
    loadTrustMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearch, dateFrom, dateTo, page, refreshTrigger]);

  // Reset to page 0 when search changes
  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearch]);

  const loadTrustMasters = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        pageSize: pageSize,
      };

      if (appliedSearch.trim()) {
        params.searchTerm = appliedSearch.trim();
      }

      if (dateFrom) {
        params.dateFrom = dateFrom;
      }

      if (dateTo) {
        params.dateTo = dateTo;
      }

      const response = await trustMastersAPI.getAll(params);

      // Handle response structure
      const data = response.data?.trustMasters || response.data || [];
      const pagination = response.data?.pagination || {};

      setTrustMasters(Array.isArray(data) ? data : []);
      setTotalCount(pagination.totalCount || data.length || 0);
    } catch (error) {
      console.error('Error loading trust masters:', error);
      showError('Failed to load trust masters');
      setTrustMasters([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setAppliedSearch(searchInput);
    setDateFrom(dateFromInput);
    setDateTo(dateToInput);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setAppliedSearch('');
    setDateFromInput('');
    setDateToInput('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  const hasFilterChanges =
    searchInput !== appliedSearch || dateFromInput !== dateFrom || dateToInput !== dateTo;
  const hasActiveFilters = appliedSearch || dateFrom || dateTo;

  const handlePreviousPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = page * pageSize + 1;
  const endIndex = Math.min((page + 1) * pageSize, totalCount);

  return (
    <Box>
      {/* Filters and Pagination */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '4px',
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          {/* Search Input */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search trust"
              value={searchInput}
              autoComplete="off"
              onKeyPress={handleFilterKeyPress}
              InputProps={{
                endAdornment: <FiSearch style={{ marginRight: 8, color: colors.text.secondary }} />,
              }}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </Grid>

          {/* Date From Filter */}
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Created From"
              value={dateFromInput}
              onChange={(e) => setDateFromInput(e.target.value)}
              onKeyPress={handleFilterKeyPress}
              InputLabelProps={{ shrink: true }}
              onClick={(e) => e.target.showPicker?.()}
              sx={{ cursor: 'pointer' }}
            />
          </Grid>

          {/* Date To Filter */}
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Created To"
              value={dateToInput}
              onChange={(e) => setDateToInput(e.target.value)}
              onKeyPress={handleFilterKeyPress}
              InputLabelProps={{ shrink: true }}
              onClick={(e) => e.target.showPicker?.()}
              sx={{ cursor: 'pointer' }}
            />
          </Grid>

          {/* Apply/Clear Buttons */}
          <Grid item xs={12} md={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                size="small"
                onClick={handleApplyFilters}
                disabled={!hasFilterChanges}
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '8px',
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Apply
              </Button>
              <Button
                size="small"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '8px',
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Clear
              </Button>
            </Box>
          </Grid>

          {/* Pagination Controls */}
          <Grid item xs={12} md={3}>
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
              <IconButton
                size="small"
                onClick={handlePreviousPage}
                disabled={page === 0}
                sx={{
                  borderRadius: 1,
                  marginRight: -2,
                }}
              >
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  color: colors.text.secondary,
                  minWidth: '120px',
                  textAlign: 'center',
                }}
              >
                {totalCount > 0 ? `${startIndex}-${endIndex} of ${totalCount}` : '0 of 0'}
              </Typography>
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={page >= totalPages - 1 || totalCount === 0}
                sx={{
                  borderRadius: 1,
                  marginLeft: -2,
                }}
              >
                <FiChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table or Empty State */}
      {loading ? (
        <TableLoader message="Loading trust masters..." />
      ) : trustMasters.length === 0 ? (
        <Paper
          sx={{
            border: 'none !important',
            boxShadow: 'none',
            py: 4,
            textAlign: 'center',
            backgroundColor: 'transparent !important',
          }}
        >
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            {appliedSearch || hasActiveFilters
              ? 'No trust masters found matching your filters'
              : 'No trust masters found.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: '4px',
            boxShadow: 'none',
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: colors.background.default,
                  borderBottom: `1px solid ${colors.border.main}`,
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.875rem',
                  }}
                >
                  Trust Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.875rem',
                  }}
                >
                  Receipt Prefix
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.875rem',
                  }}
                >
                  Created By
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.875rem',
                  }}
                >
                  Created Date
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.875rem',
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trustMasters.map((trustMaster) => (
                <TableRow
                  key={trustMaster.id}
                  onClick={() => onView(trustMaster)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: colors.action.row,
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      maxWidth: '250px',
                    }}
                  >
                    <Tooltip
                      title={trustMaster.trustName || trustMaster.trust_name || '-'}
                      arrow
                      placement="top"
                    >
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.875rem',
                          maxWidth: '250px',
                          display: 'block',
                        }}
                      >
                        {trustMaster.trustName || trustMaster.trust_name || '-'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                    }}
                  >
                    {trustMaster.receiptPrefix || trustMaster.receipt_prefix || '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                    }}
                  >
                    {trustMaster.createdByName || trustMaster.created_by_name || '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                    }}
                  >
                    {trustMaster.createdAt || trustMaster.created_at
                      ? formatDateForDisplay(trustMaster.createdAt || trustMaster.created_at)
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'center',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Preview Receipt">
                        <IconButton
                          size="small"
                          onClick={() => onPreviewReceipt(trustMaster)}
                          sx={{
                            color: colors.text.primary,
                            '&:hover': {
                              color: colors.primary.main,
                            },
                          }}
                        >
                          <FiFileText size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(trustMaster)}
                          sx={{
                            color: colors.text.primary,
                            '&:hover': {
                              color: colors.primary.dark,
                            },
                          }}
                        >
                          <FiEdit2 size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          trustMaster.can_delete
                            ? 'Delete'
                            : 'Cannot delete - Trust is being used in pledges and transactions'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onDelete(trustMaster)}
                            disabled={!trustMaster.can_delete}
                            sx={{
                              color: colors.text.primary,
                              '&:hover': {
                                color: colors.error.main,
                              },
                              '&:disabled': {
                                color: colors.text.disabled,
                                cursor: 'not-allowed',
                              },
                            }}
                          >
                            <FiTrash2 size={18} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
